import React, {useMemo} from 'react';
import ReactECharts from 'echarts-for-react';
import {Alert, Box, useTheme} from '@mui/material';
import type {EChartsOption} from 'echarts';

type Props = {
    config: string;
}
export const ChartRenderer: React.FC<Props> = React.memo(({config}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    const chartOption = useMemo(() => {
        try {
            const parsed = JSON.parse(config);

            // Apply theme-aware defaults
            const defaultOption: Partial<EChartsOption> = {
                backgroundColor: 'transparent',
                textStyle: {
                    color: isDark ? '#ffffff' : '#1a1a1a',
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true,
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                },
                tooltip: {
                    backgroundColor: isDark ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    textStyle: {
                        color: isDark ? '#ffffff' : '#1a1a1a',
                    },
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                        lineStyle: {
                            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        },
                    },
                },
                legend: {
                    textStyle: {
                        color: isDark ? '#ffffff' : '#1a1a1a',
                    },
                    top: '5%',
                },
            };

            // Deep merge the default options with parsed config
            const mergedOption: EChartsOption = {
                ...defaultOption,
                ...parsed,
                textStyle: {
                    ...defaultOption.textStyle,
                    ...parsed.textStyle,
                },
                grid: {
                    ...defaultOption.grid,
                    ...parsed.grid,
                },
                tooltip: {
                    ...defaultOption.tooltip,
                    ...parsed.tooltip,
                },
                legend: {
                    ...defaultOption.legend,
                    ...parsed.legend,
                },
            };

            return mergedOption;
        } catch (error) {
            console.error('Error parsing chart configuration:', error);
            return null;
        }
    }, [config, isDark]);

    if (!chartOption) {
        return (
            <Alert
                severity="error"
                sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.3)',
                    color: '#f44336',
                    mb: 2,
                }}
            >
                Invalid chart configuration. Please check the JSON format.
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: 400,
                my: 2,
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                borderRadius: 2,
                backgroundColor: isDark ? 'rgba(26, 26, 26, 0.5)' : 'rgba(248, 250, 252, 0.5)',
                overflow: 'hidden',
            }}
        >
            <ReactECharts
                option={chartOption}
                style={{
                    height: '100%',
                    width: '100%',
                }}
                opts={{
                    renderer: 'svg',
                }}
            />
        </Box>
    );
});
